import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

// Card type detection based on card number patterns
export const detectCardType = (cardNumber: string): string => {
  // Remove spaces and non-digits
  const cleanNumber = cardNumber.replace(/\D/g, '');
  
  // Card type patterns
  const patterns = {
    visa: /^4[0-9]{12}([0-9]{3})?$/,
    mastercard: /^5[1-5][0-9]{14}$|^2(?:2(?:2[1-9]|[3-9][0-9])|[3-6][0-9][0-9]|7(?:[01][0-9]|20))[0-9]{12}$/,
    amex: /^3[47][0-9]{13}$/,
    discover: /^6(?:011|5[0-9]{2})[0-9]{12}$/,
    diners: /^3(?:0[0-5]|[68][0-9])[0-9]{11}$/,
    jcb: /^(?:2131|1800|35\d{3})\d{11}$/,
    unionpay: /^(62|88)\d{14,17}$/
  };
  
  for (const [type, pattern] of Object.entries(patterns)) {
    if (pattern.test(cleanNumber)) {
      return type;
    }
  }
  
  return 'unknown';
};

// Format card number with spaces
export const formatCardNumber = (value: string): string => {
  const cleanValue = value.replace(/\D/g, '');
  const cardType = detectCardType(cleanValue);
  
  // AMEX has different formatting (4-6-5)
  if (cardType === 'amex') {
    return cleanValue.replace(/(\d{4})(\d{6})(\d{5})/, '$1 $2 $3');
  }
  
  // Standard formatting (4-4-4-4)
  return cleanValue.replace(/(\d{4})(?=\d)/g, '$1 ');
};

// Validate card expiry
export const isValidExpiry = (month: number, year: number): boolean => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  
  if (year < currentYear) return false;
  if (year === currentYear && month < currentMonth) return false;
  
  return true;
};

// Calculate payment fees
export const calculatePaymentFees = async (
  bidAmount: number, 
  cardType: string, 
  isInternational: boolean = false
): Promise<{
  baseFeeAmount: number;
  perTransactionFee: number;
  internationalFee: number;
  totalFees: number;
  totalAmount: number;
}> => {
  try {
    const { data, error } = await supabase.rpc('calculate_payment_fees', {
      bid_amount: bidAmount,
      card_type_param: cardType.toLowerCase(),
      is_international: isInternational
    });

    if (error) throw error;

    const result = data[0];
    return {
      baseFeeAmount: parseFloat(result.base_fee_amount),
      perTransactionFee: parseFloat(result.per_transaction_fee),
      internationalFee: parseFloat(result.international_fee),
      totalFees: parseFloat(result.total_fees),
      totalAmount: parseFloat(result.total_amount)
    };
  } catch (error) {
    console.error('Error calculating payment fees:', error);
    // Return default Visa rates as fallback
    const baseFee = bidAmount * 0.0285;
    const transactionFee = 0.30;
    const intlFee = isInternational ? bidAmount * 0.01 : 0;
    const totalFees = baseFee + transactionFee + intlFee;
    
    return {
      baseFeeAmount: Math.round(baseFee * 100) / 100,
      perTransactionFee: transactionFee,
      internationalFee: Math.round(intlFee * 100) / 100,
      totalFees: Math.round(totalFees * 100) / 100,
      totalAmount: Math.round((bidAmount + totalFees) * 100) / 100
    };
  }
};

// Get user's payment methods
type UserPaymentMethodRow = Database['public']['Tables']['user_payment_methods']['Row'];

export const getUserPaymentMethods = async (userId: string): Promise<UserPaymentMethodRow[]> => {
  try {
    const { data, error } = await supabase
      .from('user_payment_methods')
      .select('*')
      .eq('user_id', userId)
      .eq('is_verified', true)
      .order('is_primary', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching payment methods:', error);
    return [];
  }
};

// Check if user has verified payment method
export const userHasVerifiedPaymentMethod = async (userId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('user_payment_methods')
      .select('id')
      .eq('user_id', userId)
      .eq('is_verified', true)
      .limit(1);

    if (error) throw error;
    return (data?.length || 0) > 0;
  } catch (error) {
    console.error('Error checking payment method:', error);
    return false;
  }
};

// Format currency
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

// Get card brand icon/emoji
export const getCardBrandIcon = (cardType: string): string => {
  const icons: Record<string, string> = {
    visa: '💳',
    mastercard: '💳', 
    amex: '💼',
    discover: '🔍',
    diners: '🍽️',
    jcb: '🌸',
    unionpay: '🇨🇳'
  };
  
  return icons[cardType.toLowerCase()] || '💳';
};

// Get card brand name
export const getCardBrandName = (cardType: string): string => {
  const names: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    diners: 'Diners Club',
    jcb: 'JCB',
    unionpay: 'UnionPay'
  };
  
  return names[cardType.toLowerCase()] || cardType.toUpperCase();
};

// Validate bid with payment method
export const validateBidWithPayment = async (
  userId: string,
  lotId: string,
  bidAmount: number
): Promise<{
  valid: boolean;
  message?: string;
  requiresPaymentMethod?: boolean;
  estimatedTotal?: number;
  fees?: PaymentFeeEstimate;
}> => {
  try {
    // Check if user has verified payment method
    const hasPaymentMethod = await userHasVerifiedPaymentMethod(userId);
    
    if (!hasPaymentMethod) {
      return {
        valid: false,
        message: 'A verified payment method is required to place bids',
        requiresPaymentMethod: true
      };
    }

    // Get user's primary payment method
    const { data: paymentMethod, error } = await supabase
      .from('user_payment_methods')
      .select('*')
      .eq('user_id', userId)
      .eq('is_verified', true)
      .order('is_primary', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(1);

    if (error || !paymentMethod?.[0]) {
      return {
        valid: false,
        message: 'Unable to verify payment method',
        requiresPaymentMethod: true
      };
    }

    // Calculate fees
    const fees = await calculatePaymentFees(bidAmount, paymentMethod[0].card_type ?? '');

    return {
      valid: true,
      estimatedTotal: fees.totalAmount,
      fees: fees
    };
  } catch (error) {
    console.error('Error validating bid with payment:', error);
    return {
      valid: false,
      message: 'Error validating payment method'
    };
  }
};

// Force refresh payment method status (useful after adding new cards)
export const refreshPaymentMethodStatus = async (userId: string): Promise<{
  hasPaymentMethod: boolean;
  primaryMethod: Database['public']['Tables']['user_payment_methods']['Row'] | null;
  allMethods: Database['public']['Tables']['user_payment_methods']['Row'][];
}> => {
  try {
    // Get all verified payment methods
    const { data: methods, error } = await supabase
      .from('user_payment_methods')
      .select('*')
      .eq('user_id', userId)
      .eq('is_verified', true)
      .order('is_primary', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) throw error;

    const hasPaymentMethod = (methods?.length || 0) > 0;
    const primaryMethod = hasPaymentMethod ? methods[0] : null;

    console.log('🔄 Payment method status refreshed:', { 
      userId, 
      hasPaymentMethod, 
      methodCount: methods?.length || 0 
    });

    return {
      hasPaymentMethod,
      primaryMethod,
      allMethods: methods || []
    };
  } catch (error) {
    console.error('Error refreshing payment method status:', error);
    return {
      hasPaymentMethod: false,
      primaryMethod: null,
      allMethods: []
    };
  }
}; 